/**
 * Card Modal Component
 * 
 * Transforms cards into modal dialogs with backdrop and centering
 * 
 * Usage:
 * - Add data-card-modal="modal-id" to a button/trigger element
 * - Add id="modal-id" and class="card-modal" to the card element
 * - Optionally add size classes: small, medium, large, xlarge, full, auto
 * - Optionally add animation classes: slide-up, zoom-in, fade
 */

(function() {
    'use strict';

    // Card Modal Manager
    const CardModal = {
        activeModals: [],
        scrollbarWidth: 0,

        // Initialize card modals
        init() {
            this.calculateScrollbarWidth();
            this.bindTriggers();
            this.bindCloseButtons();
            this.bindBackdropClose();
            this.bindEscapeKey();
        },

        // Calculate scrollbar width for body padding adjustment
        calculateScrollbarWidth() {
            const outer = document.createElement('div');
            outer.style.visibility = 'hidden';
            outer.style.overflow = 'scroll';
            document.body.appendChild(outer);

            const inner = document.createElement('div');
            outer.appendChild(inner);

            this.scrollbarWidth = outer.offsetWidth - inner.offsetWidth;
            outer.parentNode.removeChild(outer);
        },

        // Bind modal triggers
        bindTriggers() {
            document.addEventListener('click', (e) => {
                const trigger = e.target.closest('[data-card-modal]');
                if (trigger) {
                    e.preventDefault();
                    const modalId = trigger.dataset.cardModal;
                    this.open(modalId);
                }
            });
        },

        // Bind close buttons
        bindCloseButtons() {
            document.addEventListener('click', (e) => {
                if (e.target.closest('.close-modal') || e.target.closest('[data-close-modal]')) {
                    e.preventDefault();
                    const modal = e.target.closest('.card-modal');
                    if (modal) {
                        this.close(modal.id);
                    }
                }
            });
        },

        // Bind backdrop close
        bindBackdropClose() {
            document.addEventListener('click', (e) => {
                if (e.target.classList.contains('card-modal-backdrop')) {
                    const backdrop = e.target;
                    const modalId = backdrop.dataset.modalId;
                    if (modalId && !backdrop.dataset.static) {
                        this.close(modalId);
                    }
                }
            });
        },

        // Bind escape key
        bindEscapeKey() {
            document.addEventListener('keydown', (e) => {
                if (e.key === 'Escape' && this.activeModals.length > 0) {
                    const lastModal = this.activeModals[this.activeModals.length - 1];
                    const modal = document.getElementById(lastModal);
                    if (modal && !modal.dataset.noEscape) {
                        this.close(lastModal);
                    }
                }
            });
        },

        // Open a modal
        open(modalId, options = {}) {
            const modal = document.getElementById(modalId);
            if (!modal || !modal.classList.contains('card-modal')) {
                console.error(`Card modal with id "${modalId}" not found`);
                return;
            }

            // Create or get backdrop
            let backdrop = document.querySelector(`.card-modal-backdrop[data-modal-id="${modalId}"]`);
            if (!backdrop) {
                backdrop = this.createBackdrop(modalId, options.staticBackdrop);
            }

            // Move modal to end of body to ensure it's after backdrop
            document.body.appendChild(modal);

            // Set stack index for multiple modals
            const stackIndex = this.activeModals.length;
            modal.style.setProperty('--stack-index', stackIndex);
            backdrop.style.setProperty('--stack-index', stackIndex);

            // Add to active modals
            this.activeModals.push(modalId);

            // Prevent body scroll
            if (this.activeModals.length === 1) {
                document.body.style.setProperty('--scrollbar-width', this.scrollbarWidth + 'px');
                document.body.classList.add('card-modal-open');
            }

            // Show backdrop and modal
            backdrop.classList.add('show');
            setTimeout(() => {
                modal.classList.add('show');
                this.focusTrap(modal);
                
                // Trigger custom event
                modal.dispatchEvent(new CustomEvent('cardmodal:opened', { detail: { modalId } }));
            }, 10);

            // Call onOpen callback if provided
            if (options.onOpen && typeof options.onOpen === 'function') {
                options.onOpen(modal);
            }

            return modal;
        },

        // Close a modal
        close(modalId, options = {}) {
            const modal = document.getElementById(modalId);
            const backdrop = document.querySelector(`.card-modal-backdrop[data-modal-id="${modalId}"]`);

            if (!modal || !backdrop) return;

            // Remove from active modals
            const index = this.activeModals.indexOf(modalId);
            if (index > -1) {
                this.activeModals.splice(index, 1);
            }

            // Hide modal and backdrop
            modal.classList.remove('show');
            backdrop.classList.remove('show');

            setTimeout(() => {
                backdrop.remove();
                
                // Restore body scroll if no more modals
                if (this.activeModals.length === 0) {
                    document.body.classList.remove('card-modal-open');
                    document.body.style.removeProperty('--scrollbar-width');
                }

                // Trigger custom event
                modal.dispatchEvent(new CustomEvent('cardmodal:closed', { detail: { modalId } }));

                // Call onClose callback if provided
                if (options.onClose && typeof options.onClose === 'function') {
                    options.onClose(modal);
                }
            }, 300);
        },

        // Create backdrop element
        createBackdrop(modalId, isStatic = false) {
            const backdrop = document.createElement('div');
            backdrop.className = 'card-modal-backdrop';
            backdrop.dataset.modalId = modalId;
            if (isStatic) {
                backdrop.dataset.static = 'true';
            }
            document.body.appendChild(backdrop);
            return backdrop;
        },

        // Focus trap for accessibility
        focusTrap(modal) {
            const focusableElements = modal.querySelectorAll(
                'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
            );

            if (focusableElements.length === 0) return;

            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];

            // Focus first element
            firstElement.focus();

            // Trap focus within modal
            modal.addEventListener('keydown', function trapFocus(e) {
                if (e.key !== 'Tab') return;

                if (e.shiftKey) {
                    if (document.activeElement === firstElement) {
                        lastElement.focus();
                        e.preventDefault();
                    }
                } else {
                    if (document.activeElement === lastElement) {
                        firstElement.focus();
                        e.preventDefault();
                    }
                }
            });
        },

        // Utility: Show confirmation modal
        confirm(options = {}) {
            const {
                title = 'Confirm',
                message = 'Are you sure?',
                submessage = '',
                confirmText = 'Confirm',
                cancelText = 'Cancel',
                type = 'warning', // warning, danger, success, info
                onConfirm = () => {},
                onCancel = () => {}
            } = options;

            // Create modal HTML
            const modalId = 'confirm-modal-' + Date.now();
            const modalHtml = `
                <div id="${modalId}" class="card-modal small zoom-in theme-dark" style="display: none;">
                    <div class="card">
                        <div class="card-header">
                            <div class="card-title">
                                <h4>${title}</h4>
                            </div>
                            <button class="close-modal" aria-label="Close">
                                <span aria-hidden="true">&times;</span>
                            </button>
                        </div>
                        <div class="card-section card-modal-confirm">
                            <div class="confirm-icon ${type}">
                                ${this.getIcon(type)}
                            </div>
                            <div class="confirm-message">${message}</div>
                            ${submessage ? `<div class="confirm-submessage">${submessage}</div>` : ''}
                        </div>
                        <div class="card-footer">
                            <button class="button secondary" data-action="cancel">${cancelText}</button>
                            <button class="button ${this.getButtonClass(type)}" data-action="confirm">${confirmText}</button>
                        </div>
                    </div>
                </div>
            `;

            // Add modal to DOM
            document.body.insertAdjacentHTML('beforeend', modalHtml);
            const modal = document.getElementById(modalId);

            // Bind actions
            modal.querySelector('[data-action="confirm"]').addEventListener('click', () => {
                this.close(modalId);
                onConfirm();
                setTimeout(() => modal.remove(), 300);
            });

            modal.querySelector('[data-action="cancel"]').addEventListener('click', () => {
                this.close(modalId);
                onCancel();
                setTimeout(() => modal.remove(), 300);
            });

            // Open modal
            this.open(modalId, { staticBackdrop: true });

            return modal;
        },

        // Utility: Show alert modal
        alert(options = {}) {
            const {
                title = 'Alert',
                message = '',
                type = 'info',
                buttonText = 'OK',
                onClose = () => {}
            } = options;

            const modalId = 'alert-modal-' + Date.now();
            const modalHtml = `
                <div id="${modalId}" class="card-modal small slide-up theme-dark" style="display: none;">
                    <div class="card">
                        <div class="card-header">
                            <div class="card-title">
                                <h4>${title}</h4>
                            </div>
                            <button class="close-modal" aria-label="Close">
                                <span aria-hidden="true">&times;</span>
                            </button>
                        </div>
                        <div class="card-section">
                            <div class="alert alert-${type}">
                                ${message}
                            </div>
                        </div>
                        <div class="card-footer center">
                            <button class="button ${this.getButtonClass(type)}" data-close-modal>${buttonText}</button>
                        </div>
                    </div>
                </div>
            `;

            document.body.insertAdjacentHTML('beforeend', modalHtml);
            const modal = document.getElementById(modalId);

            modal.querySelector('[data-close-modal]').addEventListener('click', () => {
                this.close(modalId);
                onClose();
                setTimeout(() => modal.remove(), 300);
            });

            this.open(modalId);
            return modal;
        },

        // Get icon for type
        getIcon(type) {
            const icons = {
                warning: '<i class="fas fa-exclamation-triangle"></i>',
                danger: '<i class="fas fa-exclamation-circle"></i>',
                success: '<i class="fas fa-check-circle"></i>',
                info: '<i class="fas fa-info-circle"></i>'
            };
            return icons[type] || icons.info;
        },

        // Get button class for type
        getButtonClass(type) {
            const classes = {
                warning: 'warning',
                danger: 'alert',
                success: 'success',
                info: 'primary'
            };
            return classes[type] || 'primary';
        }
    };

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => CardModal.init());
    } else {
        CardModal.init();
    }

    // Export to global scope
    window.CardModal = CardModal;
})();